import './loanDocumentUploadPage.scss';

import { ThemeProvider } from '@mui/material/styles';
import { splitFilename } from '@thesavingsgroup/utils/splitFilename';
import forEach from 'lodash/forEach';
import initial from 'lodash/initial';
import reduce from 'lodash/reduce';
import uniqueId from 'lodash/uniqueId';
import { ReactNode, useCallback, useEffect, useState } from 'react';
import { ErrorCode, FileError, FileRejection } from 'react-dropzone';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { Alert } from '../../../components/Alert/Alert';
import { Button } from '../../../components/Button/Button';
import { Header } from '../../../components/Header/Header';
import { InfoRoundedIcon } from '../../../components/Icons/InfoRoundedIcon';
import { NavigationPane } from '../../../components/NavigationPane/NavigationPane';
import { Toast } from '../../../components/Toast/Toast';
import { Tooltip } from '../../../components/Tooltip/Tooltip';
import { LoanDocumentEnum } from '../../../store/loanDocumentStore/enums/LoanDocumentEnum';
import { useLoanDocumentStoreForType } from '../../../store/loanDocumentStore/useLoanDocumentStoreForType';
import useThemeStore from '../../../store/themeStore/useThemeStore';
import { locator } from '../../../testing/e2e-locator/locator';
import Dropzone from '../Dropzone';
import { LoanDocumentList } from '../shared/LoanDocumentList';
import { LoanDocumentUploadedList } from '../shared/LoanDocumentUploadedList';

type LoanDocumentUploadPageProps = {
  exampleDocumentUrl: string;
  subtitleText: string;
  titleText: string;
  type: LoanDocumentEnum;
};

export function Component({
  exampleDocumentUrl,
  subtitleText,
  titleText,
  type,
}: LoanDocumentUploadPageProps): ReactNode {
  const {
    addDocuments,
    confirm,
    hasProvidedDocuments,
    isDuplicateFile,
    loadDocuments,
    percent,
    providedDocuments,
    reset,
    uploadedDocuments,
  } = useLoanDocumentStoreForType(type);

  const { createTheme } = useThemeStore();
  const theme = createTheme();
  const navigate = useNavigate();

  const { t } = useTranslation();
  const { handleSubmit } = useForm();
  const tooltip = t('LoanDocumentUploadPage.tooltip');

  const [rejections, setRejections] = useState<[string, string][]>([]);
  const [removalStarted, setRemovalStarted] = useState<boolean>(false);

  const MAXIMUM_FILE_NAME_LENGTH = 12;
  const TRUNCATED_FILE_NAME_PREFIX_LENGTH = 4;
  const TRUNCATED_FILE_NAME_SUFFIX_LENGTH = 4;
  const ALERT_REMOVAL_DURATION = 500;

  useEffect((): (() => void) => {
    loadDocuments();

    // remove any unconfirmed files on unmount
    // this could be due to the back button, cancel button, or navigating away
    return (): void => {
      reset();
    };
  }, []);

  useEffect((): (() => void) => {
    let intervalId: NodeJS.Timeout;

    if (removalStarted) {
      intervalId = setInterval((): void => {
        if (rejections.length > 0) {
          setRejections(initial(rejections));
        } else {
          clearInterval(intervalId);
          setRemovalStarted(false);
        }
      }, ALERT_REMOVAL_DURATION);
    }

    return (): void => {
      clearInterval(intervalId);
    };
  }, [rejections, removalStarted]);

  const back = (): void => {
    navigate('/loan-documents/list');
  };

  const previousStep = (): void => {
    navigate('/loan-documents/list');
  };

  const onSubmit = (): void => {
    confirm();
    navigate('/loan-documents/list');
  };

  const getFileName = (fileName: string): string => {
    const { name, extension } = splitFilename(fileName);

    return name.length > MAXIMUM_FILE_NAME_LENGTH
      ? `${name.slice(0, TRUNCATED_FILE_NAME_PREFIX_LENGTH)}...${name.slice(
          -TRUNCATED_FILE_NAME_SUFFIX_LENGTH,
        )}.${extension}`
      : fileName;
  };

  const getErrorMessage = (error: FileError): string => {
    switch (error.code) {
      case ErrorCode.FileInvalidType:
        return t('dropzone.rejectedFileType');
      case ErrorCode.FileTooLarge:
        return t('dropzone.rejectedFileTooLarge');
      default:
        return error.message;
    }
  };

  const onDrop = useCallback(
    (acceptedFiles: File[], rejectedFiles: FileRejection[]): void => {
      addDocuments(acceptedFiles);

      if (rejectedFiles.length !== 0) {
        const rejectionList = reduce(
          rejectedFiles,
          (
            list: [string, string][],
            rejectedFile: FileRejection,
          ): [string, string][] => {
            forEach(rejectedFile.errors, (error: FileError): void => {
              const fileName = getFileName(rejectedFile.file.name);
              const message = getErrorMessage(error);

              list.push([uniqueId('rejection-'), `${fileName}: ${message}`]);
            });

            return list;
          },
          [],
        );

        setRejections(rejectionList);
      }
    },
    [],
  );

  const clearRejections = (): void => {
    setRemovalStarted(true);
  };

  return (
    <ThemeProvider theme={theme}>
      <div className="page loan-document-upload-page">
        <Toast
          open={rejections.length !== 0}
          placement="left"
          onClose={clearRejections}
        >
          <div className="rejection-alerts">
            {rejections.map(
              ([key, message]): ReactNode => (
                <Alert
                  className="rejection-alert"
                  data-testid={locator.pages.loanDocumentUpload.alert.error}
                  key={key}
                  variant="filled"
                >
                  {message}
                </Alert>
              ),
            )}
          </div>
        </Toast>

        <Header
          progress={percent}
          showDrawerToggle
          onBackClick={previousStep}
        />

        <main>
          <form
            data-testid={locator.pages.loanDocumentUpload.form.root}
            noValidate
            onSubmit={handleSubmit(onSubmit)}
          >
            <div className="form-content">
              <h1 data-testid={locator.pages.loanDocumentUpload.form.h1}>
                {titleText}
              </h1>
              <h2 data-testid={locator.pages.loanDocumentUpload.form.h2}>
                {subtitleText}
              </h2>

              <div
                className="loan-document-example"
                data-testid={locator.pages.loanDocumentUpload.form.exampleRoot}
              >
                <span>
                  <a
                    data-testid={
                      locator.pages.loanDocumentUpload.form.exampleLink
                    }
                    href={exampleDocumentUrl}
                    rel="noreferrer"
                    target="_blank"
                  >
                    {t('LoanDocumentUploadPage.exampleDocument')}
                  </a>
                </span>
                <Tooltip title={tooltip}>
                  <button
                    aria-label={tooltip}
                    className="info-button"
                    type="button"
                  >
                    <InfoRoundedIcon className="info-icon" />
                  </button>
                </Tooltip>
              </div>

              <div style={{ marginBottom: '3rem' }}>
                <Dropzone isDuplicate={isDuplicateFile} onDrop={onDrop} />

                <LoanDocumentList providedDocuments={providedDocuments} />

                {uploadedDocuments.length > 0 && (
                  <LoanDocumentUploadedList
                    uploadedDocuments={uploadedDocuments}
                  />
                )}
              </div>

              <div className="inline-buttons">
                <Button
                  data-testid={
                    locator.pages.loanDocumentUpload.form.cancelButton
                  }
                  variant="secondary"
                  fullWidth
                  onClick={back}
                >
                  {t('LoanDocumentUploadPage.cancel')}
                </Button>

                <Button
                  data-testid={
                    locator.pages.loanDocumentUpload.form.submitButton
                  }
                  disabled={!hasProvidedDocuments}
                  fullWidth
                  submit
                >
                  {t('LoanDocumentUploadPage.submit')}
                </Button>
              </div>
            </div>
            <footer data-testid={locator.pages.loanDocumentUpload.footer.root}>
              {t('copyright', { year: new Date().getFullYear() })}
            </footer>
          </form>
        </main>
      </div>

      <NavigationPane withDrawer withUserNameHeader />
    </ThemeProvider>
  );
}

Component.displayName = 'LoanDocumentUploadPage';
